import React, { useMemo, useState } from 'react';
import { Wallet } from 'lucide-react';
import { useUnifiedCustomers } from '../../lib/workspaceStore';
import { PageSearchBar } from '../../components/common/PageSearchBar';
import { inRange } from '../../lib/dateFilter';
import { useDashboardDetailDates } from '../../lib/useDashboardDetailDates';
import type { ExportRow } from '../../lib/exportReports';
import { DashboardDetailShell, DashboardMetricCard, type DashboardDetailDateProps } from './DashboardDetailShell';

interface TotalLoanAmountProps extends DashboardDetailDateProps {
  onClose: () => void;
}

export function TotalLoanAmount({ onClose, initialDateRange }: TotalLoanAmountProps) {
  const customers = useUnifiedCustomers();
  const dr = useDashboardDetailDates(initialDateRange);
  const [query, setQuery] = useState('');

  const allLoans = useMemo(() => {
    const rows: {
      id: string;
      name: string;
      displayRef: string;
      loanAmount: number;
      date: string;
      tenure: string;
      purpose: string;
      title: string;
    }[] = [];
    for (const c of customers) {
      for (const f of c.facilities) {
        if (!inRange(f.startDate, dr.range)) continue;
        rows.push({
          id: `${c.id}-${f.recordId}`,
          name: c.name,
          displayRef: c.displayRef || c.phone || c.name,
          loanAmount: f.principal,
          date: f.startDate,
          tenure: `${f.periods} mo`,
          purpose: f.kind === 'finance' ? 'Finance' : 'Hand loan',
          title: f.title,
        });
      }
    }
    return rows.sort((a, b) => b.loanAmount - a.loanAmount);
  }, [customers, dr.range]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allLoans;
    return allLoans.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.displayRef.toLowerCase().includes(q) ||
        r.title.toLowerCase().includes(q) ||
        r.purpose.toLowerCase().includes(q)
    );
  }, [allLoans, query]);

  const loanSummary = useMemo(() => {
    const totalAmount = filtered.reduce((s, r) => s + r.loanAmount, 0);
    const averageLoanAmount = filtered.length > 0 ? Math.round(totalAmount / filtered.length) : 0;
    return { totalAmount, activeLoans: filtered.length, averageLoanAmount };
  }, [filtered]);

  const exportRows: ExportRow[] = useMemo(
    () =>
      filtered.map((r) => ({
        Customer: r.name,
        Mobile: r.displayRef,
        Facility: r.title,
        Kind: r.purpose,
        Principal: r.loanAmount,
        StartDate: r.date,
        Tenure: r.tenure,
      })),
    [filtered]
  );

  return (
    <DashboardDetailShell
      onClose={onClose}
      title="Total loan amount"
      subtitle="Facilities started in the selected period."
      icon={Wallet}
      iconClassName="text-amber-700"
      exportTitle="Loan Portfolio Report"
      exportFilename="total-loan-amount.xls"
      exportRows={exportRows}
      dateRange={dr.range}
      datePreset={dr.preset}
      onDateStart={dr.setStart}
      onDateEnd={dr.setEnd}
      onDatePreset={dr.setPreset}
      onDateClear={dr.clear}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <DashboardMetricCard label="Principal booked" value={`₹${loanSummary.totalAmount.toLocaleString('en-IN')}`} accent />
        <DashboardMetricCard label="Facilities" value={loanSummary.activeLoans} />
        <DashboardMetricCard label="Avg / facility" value={`₹${loanSummary.averageLoanAmount.toLocaleString('en-IN')}`} />
      </div>

      <PageSearchBar
        id="dash-total-loan-search"
        value={query}
        onChange={setQuery}
        placeholder="Search customer, mobile, facility…"
        className="max-w-md"
      />

      <div className="app-card overflow-hidden shadow-sm ring-1 ring-zinc-950/[0.03]">
        <div className="border-b border-zinc-200 bg-zinc-50/90 px-4 py-3">
          <h2 className="text-sm font-semibold text-zinc-900">Facilities in period</h2>
          <p className="text-xs text-zinc-500">{filtered.length} row{filtered.length === 1 ? '' : 's'}</p>
        </div>
        <div className="app-table-wrap border-0">
          <table className="app-table min-w-full">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Mobile / ID</th>
                <th>Facility</th>
                <th>Kind</th>
                <th className="text-right">Principal</th>
                <th>Start</th>
                <th>Tenor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-sm text-zinc-500">
                    {allLoans.length === 0
                      ? 'No facilities started in this date range.'
                      : 'No rows match your search.'}
                  </td>
                </tr>
              ) : (
                filtered.map((loan) => (
                  <tr key={loan.id} className="transition-colors hover:bg-amber-50/30">
                    <td className="font-medium text-zinc-800">{loan.name}</td>
                    <td className="whitespace-nowrap font-mono text-xs text-zinc-600">{loan.displayRef}</td>
                    <td className="text-sm text-zinc-700">{loan.title}</td>
                    <td className="whitespace-nowrap text-sm text-zinc-600">{loan.purpose}</td>
                    <td className="whitespace-nowrap text-right font-semibold tabular-nums text-zinc-900">
                      ₹{loan.loanAmount.toLocaleString('en-IN')}
                    </td>
                    <td className="whitespace-nowrap text-sm text-zinc-600">{loan.date}</td>
                    <td className="whitespace-nowrap text-sm text-zinc-600">{loan.tenure}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardDetailShell>
  );
}
